
import { useState, useEffect } from 'react';
import { useAccount } from 'wagmi';
import { InitiationStep } from './types';
import { useInitiationSetup } from './useInitiationSetup';
import { useWalletInitialized } from './useWalletInitialized';
import { useTokenCheck } from './useTokenCheck';
import { useWalletUtils } from './useWalletUtils';

export const useInitiationState = () => {
  const { address, isConnected } = useAccount();
  const [currentStep, setCurrentStep] = useState<InitiationStep>(InitiationStep.CONNECT_WALLET);
  const [completionRank, setCompletionRank] = useState<string>("");
  const [completionBrocode, setCompletionBrocode] = useState<string>("");
  
  // Import functionality from our smaller hooks
  const { tableSetupDone } = useInitiationSetup();
  const { alreadyInitialized, completionRank: initialRank, completionBrocode: initialBrocode, initialStep } = useWalletInitialized();
  const { hasToken, isCheckingToken, isConnectedToBSC, handleRecheckWallet } = useTokenCheck(alreadyInitialized);
  const { handleDisconnectWallet } = useWalletUtils();

  // Update step when wallet is connected
  useEffect(() => {
    if (isConnected && currentStep === InitiationStep.CONNECT_WALLET) {
      setCurrentStep(InitiationStep.TOKEN_CHECK);
    }
  }, [isConnected, currentStep]);

  // Set initial step based on wallet initialized state
  useEffect(() => {
    if (initialStep !== null) {
      setCurrentStep(initialStep);
    }
  }, [initialStep]);

  // Set initial rank and brocode if already initialized
  useEffect(() => {
    if (initialRank) {
      setCompletionRank(initialRank);
    }
    if (initialBrocode) {
      setCompletionBrocode(initialBrocode);
    }
  }, [initialRank, initialBrocode]);

  // Handler for proceeding to path selection after token check
  const handleProceedToPathSelection = () => {
    setCurrentStep(InitiationStep.PATH_SELECTION);
  };

  // Handler for selecting cipher path
  const handleSelectCipher = () => {
    setCurrentStep(InitiationStep.TAKE_QUIZ);
  };


  // Handler for selecting Twitter path
  const handleSelectTwitter = () => {
    setCurrentStep(InitiationStep.TWITTER_ENGAGEMENT);
  };

  // Handler for quiz completion
  const handleQuizCompletion = (rank: string, brocode: string) => {
    setCompletionRank(rank);
    setCompletionBrocode(brocode);
    setCurrentStep(InitiationStep.COMPLETION);
  };


  // Handler for Twitter engagement completion
  const handleTwitterCompletion = (rank: string, brocode: string) => {
    setCompletionRank(rank);
    setCompletionBrocode(brocode);
    setCurrentStep(InitiationStep.COMPLETION);
  };

  // Handler for going back to path selection
  const handleBackToPathSelection = () => {
    setCurrentStep(InitiationStep.PATH_SELECTION);
  };

  // Legacy handlers for backward compatibility
  const handleProceedAnyway = () => {
    setCurrentStep(InitiationStep.PATH_SELECTION);
  };

  const handleTakeCipher = () => {
    setCurrentStep(InitiationStep.PATH_SELECTION);
  };

  return {
    address,
    isConnected,
    isConnectedToBSC,
    currentStep,
    hasToken,
    isCheckingToken,
    completionRank,
    completionBrocode,
    tableSetupDone,
    alreadyInitialized,
    handleDisconnectWallet,
    handleRecheckWallet,
    handleProceedAnyway,
    handleProceedToPathSelection,
    handleSelectCipher,
    
    handleSelectTwitter,
    handleQuizCompletion,
    
    handleTwitterCompletion,
    handleBackToPathSelection,
    handleTakeCipher,
  };
};
